import React, { useRef, useEffect } from 'react';
import styles from './BeforeAfter.module.scss';
import Loader from '../../shared/Loader';
import { useTranslation } from 'react-i18next';
import { ReplaceIcon, DeleteIcon, UploadIcon } from '../../icons';
import ConfirmDialog from '../../shared/ConfirmDialog';
import { useBeforeAfter } from '../../../redux/hooks';
import { Photo } from '../../../redux/slices/beforeAfterSlice';
import { getCookie } from '../../../utils/cookieUtils';

// Интерфейс для размеров фотографии
interface PhotoPosition {
    width: number;
    height: number;
}

// Массив с размерами для каждой позиции фотографии в каскадном макете
const PHOTO_POSITIONS: PhotoPosition[] = [
    { width: 340, height: 220 }, // Позиция 0: Первое большое фото на всю ширину
    // Остальные фото в двух колонках с чередующимися размерами
    { width: 160, height: 220 }, { width: 160, height: 100 }, // 1-2
    { width: 160, height: 100 }, { width: 160, height: 100 }, // 3-4
    { width: 160, height: 220 }, { width: 160, height: 220 }, // 5-6
    { width: 160, height: 220 }, { width: 160, height: 220 }, // 7-8
    { width: 160, height: 220 }, { width: 160, height: 100 }, // 9-10
    { width: 160, height: 220 }, { width: 160, height: 100 }, // 11-12
    { width: 160, height: 100 }, { width: 160, height: 100 }, // 13-14
    { width: 160, height: 100 }, { width: 160, height: 220 }, // 15-16
    { width: 160, height: 220 }, { width: 160, height: 100 }, // 17-18
    { width: 160, height: 220 }, { width: 160, height: 220 }, // 19-20
    { width: 160, height: 100 }, { width: 160, height: 220 }, // 21-22
    { width: 160, height: 100 }, { width: 160, height: 100 }, // 23-24
    { width: 160, height: 100 }, { width: 160, height: 220 }, // 25-26
    { width: 160, height: 220 }, { width: 160, height: 220 }, // 27-28
    { width: 160, height: 220 }, { width: 160, height: 220 }, // 29-30
    { width: 160, height: 100 }, // 31
];

// Компонент для отображения отдельной фотографии
const PhotoItem = ({ photo, index, onDelete, onReplace, onDragStart, onDrop }: {
    photo: Photo | null;
    index: number;
    onDelete: (id: number) => void;
    onReplace: (id: number) => void;
    onDragStart: (e: React.DragEvent, photo: Photo) => void;
    onDrop: (e: React.DragEvent, position: number) => void;
}) => {
    const { t } = useTranslation();
    const position = PHOTO_POSITIONS[index];

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    // Определяем CSS классы в зависимости от позиции
    const getPhotoClasses = () => {
        if (index === 0) {
            return `${styles.photoContainer} ${styles.firstPhoto}`;
        }

        const isNormalHeight = position.height === 100;
        const heightClass = isNormalHeight ? styles.normal : styles.tall;
        return `${styles.photoContainer} ${styles.masonryPhoto} ${heightClass}`;
    };

    return (
        <div
            className={getPhotoClasses()}
            draggable={!!photo}
            onDragStart={(e) => photo && onDragStart(e, photo)}
            onDragOver={handleDragOver}
            onDrop={(e) => onDrop(e, index)}
        >
            {photo ? (
                <>
                    <div className={styles.cardMedia}>
                        <img src={photo.url} alt={t('beforeAfter.photoAlt')} className={styles.cardImage} />
                    </div>
                    <div className={styles.cardActions}>
                        <button
                            className={styles.iconButton}
                            onClick={() => onReplace(index)}
                            title={t('common.replace')}
                        >
                            <ReplaceIcon />
                        </button>
                        <button
                            className={styles.iconButton}
                            onClick={() => onDelete(photo.id)}
                            title={t('common.delete')}
                        >
                            <DeleteIcon />
                        </button>
                    </div>
                </>
            ) : (
                <div
                    className={styles.emptyContainer}
                    onClick={() => onReplace(index)}
                >
                    <UploadIcon />
                    <span>{t('beforeAfter.addPhoto')}</span>
                </div>
            )}
        </div>
    );
};

const BeforeAfter: React.FC = () => {
    const { t, i18n } = useTranslation();
    const language = getCookie('i18next') || 'ru';
    const {
        photos,
        isLoading,
        openReplaceDialog,
        showDeleteConfirm,
        getPhotos,
        handleReplace,
        closeReplaceDialog,
        handleReplaceFile,
        handleDelete,
        confirmDelete,
        cancelDelete,
        handleDragStart,
        handleDrop
    } = useBeforeAfter();

    // Функция для распределения фотографий по двум колонкам в Masonry стиле
    const distributePhotosIntoColumns = () => {
        const photosFromIndex1 = Array.from({ length: PHOTO_POSITIONS.length - 1 }).map((_, index) => {
            const photoIndex = index + 1; // Начинаем с индекса 1 (пропускаем первое фото)
            const photo = photos.find(p => p.position === photoIndex);
            return { photo: photo || null, index: photoIndex };
        });

        const column1: Array<{ photo: Photo | null; index: number }> = [];
        const column2: Array<{ photo: Photo | null; index: number }> = [];

        let column1Height = 0;
        let column2Height = 0;

        // Распределяем фотографии по колонкам, выбирая самую короткую колонку
        photosFromIndex1.forEach((item) => {
            const photoHeight = PHOTO_POSITIONS[item.index]?.height || 100;

            if (column1Height <= column2Height) {
                column1.push(item);
                column1Height += photoHeight + 20; // +20 для gap
            } else {
                column2.push(item);
                column2Height += photoHeight + 20; // +20 для gap
            }
        });

        return { column1, column2 };
    };

    const replaceFileInputRef = useRef<HTMLInputElement>(null);
    const replaceDialogContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (language && i18n.language !== language) {
            i18n.changeLanguage(language);
        }
    }, [language, i18n]);

    useEffect(() => {
        getPhotos();
    }, [getPhotos]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openReplaceDialog &&
                replaceDialogContentRef.current &&
                !replaceDialogContentRef.current.contains(event.target as Node)) {
                closeReplaceDialog();
            }
        };

        if (openReplaceDialog) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openReplaceDialog, closeReplaceDialog]);

    // Блокировка скролла при открытом диалоге замены фотографий
    useEffect(() => {
        if (openReplaceDialog) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [openReplaceDialog]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            handleReplaceFile(files[0]);
            if (replaceFileInputRef.current) {
                replaceFileInputRef.current.value = '';
            }
        }
    };

    const handleDragStartAdapter = (e: React.DragEvent, photo: Photo) => {
        handleDragStart(photo);
    };

    const handleDropAdapter = (e: React.DragEvent, position: number) => {
        e.preventDefault();
        handleDrop(position);
    };

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h1 className={styles.title}>{t('beforeAfter.title')}</h1>
            </header>

            {isLoading ? (
                <div className={styles.spinnerContainer}>
                    <Loader fullScreen={false} />
                </div>
            ) : (
                <div className={styles.photoGrid}>
                    {/* Первое фото на всю ширину */}
                    <PhotoItem
                        key={0}
                        photo={photos.find(p => p.position === 0) || null}
                        index={0}
                        onDelete={handleDelete}
                        onReplace={handleReplace}
                        onDragStart={handleDragStartAdapter}
                        onDrop={handleDropAdapter}
                    />

                    {/* Masonry layout для остальных фото */}
                    <div className={styles.masonryContainer}>
                        {(() => {
                            const { column1, column2 } = distributePhotosIntoColumns();

                            return (
                                <>
                                    {/* Первая колонка */}
                                    <div className={styles.masonryColumn}>
                                        {column1.map(({ photo, index }) => (
                                            <PhotoItem
                                                key={index}
                                                photo={photo}
                                                index={index}
                                                onDelete={handleDelete}
                                                onReplace={handleReplace}
                                                onDragStart={handleDragStartAdapter}
                                                onDrop={handleDropAdapter}
                                            />
                                        ))}
                                    </div>

                                    {/* Вторая колонка */}
                                    <div className={styles.masonryColumn}>
                                        {column2.map(({ photo, index }) => (
                                            <PhotoItem
                                                key={index}
                                                photo={photo}
                                                index={index}
                                                onDelete={handleDelete}
                                                onReplace={handleReplace}
                                                onDragStart={handleDragStartAdapter}
                                                onDrop={handleDropAdapter}
                                            />
                                        ))}
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            {openReplaceDialog && (
                <div className={styles.dialog}>
                    <div className={styles.dialogContent} ref={replaceDialogContentRef}>
                        <h2 className={styles.dialogTitle}>
                            {t('beforeAfter.replaceDialogTitle')}
                        </h2>
                        <div className={styles.dialogBody}>
                            <input
                                type="file"
                                ref={replaceFileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                                accept="image/*"
                            />
                            <div
                                className={styles.fileDropzone}
                                onClick={() => replaceFileInputRef.current?.click()}
                            >
                                <div className={styles.dropzoneIcon}>
                                    <UploadIcon />
                                </div>
                                <p className={styles.dropzoneText}>
                                    {t('beforeAfter.replaceDropzoneText')}
                                </p>
                            </div>
                        </div>
                        <div className={styles.dialogActions}>
                            <button
                                className={styles.cancelButton}
                                onClick={closeReplaceDialog}
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={showDeleteConfirm}
                title={t('beforeAfter.deleteDialog.title')}
                message={t('beforeAfter.deleteDialog.message')}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
        </div>
    );
};

export default BeforeAfter;
